import React, { useState, useEffect } from 'react';
import { FIREBASE_AUTH as auth } from './firebase.config';

const AdminPaystubManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [paystubs, setPaystubs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('upload'); // 'upload', 'batch', or 'manage'
  const [formData, setFormData] = useState({
    payPeriodStart: '',
    payPeriodEnd: '',
    weekNumber: '',
    year: new Date().getFullYear().toString(),
    totalHours: '',
    grossPay: '',
    netPay: '',
    deductions: '',
  });
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (view === 'manage') {
      fetchAllPaystubs();
    }
  }, [view]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        alert('Please log in as admin');
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://sbg-backend.onrender.com'}/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employees');
      }

      const data = await response.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      alert('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPaystubs = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      const token = await user.getIdToken();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://sbg-backend.onrender.com'}/api/admin/paystubs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch paystubs');
      }

      const data = await response.json();
      setPaystubs(data.paystubs || []);
    } catch (error) {
      console.error('Error fetching paystubs:', error);
      alert('Failed to load paystubs');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleUpload(file);
      }
    };
    input.click();
  };

  const handleUpload = async (file) => {
    if (!selectedEmployee) {
      alert('Please select an employee first');
      return;
    }

    if (!formData.payPeriodStart || !formData.payPeriodEnd) {
      alert('Please fill in the pay period dates');
      return;
    }

    try {
      setUploading(true);
      const user = auth.currentUser;
      const token = await user.getIdToken();

      const uploadFormData = new FormData();
      uploadFormData.append('paystub', file);
      uploadFormData.append('employeeId', selectedEmployee.employeeId);
      uploadFormData.append('payPeriodStart', formData.payPeriodStart);
      uploadFormData.append('payPeriodEnd', formData.payPeriodEnd);
      uploadFormData.append('weekNumber', formData.weekNumber);
      uploadFormData.append('year', formData.year);
      uploadFormData.append('totalHours', formData.totalHours);
      uploadFormData.append('grossPay', formData.grossPay);
      uploadFormData.append('netPay', formData.netPay);
      uploadFormData.append('deductions', formData.deductions);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://sbg-backend.onrender.com'}/api/admin/paystubs/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      alert(`Paystub uploaded successfully for ${result.employee}`);
      
      // Reset form
      setSelectedEmployee(null);
      setFormData({
        payPeriodStart: '',
        payPeriodEnd: '',
        weekNumber: '',
        year: new Date().getFullYear().toString(),
        totalHours: '',
        grossPay: '',
        netPay: '',
        deductions: '',
      });
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message || 'Failed to upload paystub');
    } finally {
      setUploading(false);
    }
  };

  const calculateWeekNumber = (startDate) => {
    if (startDate) {
      const date = new Date(startDate);
      const week = Math.ceil((date.getTime() - new Date(date.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
      setFormData(prev => ({
        ...prev,
        weekNumber: week.toString(),
        year: date.getFullYear().toString(),
      }));
    }
  };

  const handleStartDateChange = (date) => {
    setFormData(prev => ({ ...prev, payPeriodStart: date }));
    calculateWeekNumber(date);
    
    // Auto-calculate end date (6 days later)
    if (date) {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 6);
      setFormData(prev => ({ ...prev, payPeriodEnd: endDate.toISOString().split('T')[0] }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header with tabs */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Paystub Management</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setView('upload')}
            className={`px-4 py-2 rounded-lg font-medium ${
              view === 'upload'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Single Upload
          </button>
          <button
            onClick={() => setView('batch')}
            className={`px-4 py-2 rounded-lg font-medium ${
              view === 'batch'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Batch Upload
          </button>
          <button
            onClick={() => {
              setView('manage');
              fetchAllPaystubs();
            }}
            className={`px-4 py-2 rounded-lg font-medium ${
              view === 'manage'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            View All Paystubs ({paystubs.length})
          </button>
        </div>
      </div>

      {view === 'upload' && (
        <div className="space-y-6">
          {/* Employee Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Select Employee</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
              {employees.map((employee) => (
                <button
                  key={employee.employeeId}
                  onClick={() => setSelectedEmployee(employee)}
                  className={`p-3 text-left border rounded-lg transition-colors ${
                    selectedEmployee?.employeeId === employee.employeeId
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-800">{employee.employeeName}</div>
                  <div className="text-sm text-gray-600">ID: {employee.employeeId}</div>
                </button>
              ))}
            </div>
          </div>

          {selectedEmployee && (
            <>
              {/* Pay Period Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Pay Period Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Period Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.payPeriodStart}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Period End Date *
                    </label>
                    <input
                      type="date"
                      value={formData.payPeriodEnd}
                      onChange={(e) => setFormData(prev => ({ ...prev, payPeriodEnd: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Week Number
                    </label>
                    <input
                      type="number"
                      value={formData.weekNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, weekNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Pay Information (Optional) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Pay Information (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Hours
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.totalHours}
                      onChange={(e) => setFormData(prev => ({ ...prev, totalHours: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="40.0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gross Pay
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.grossPay}
                      onChange={(e) => setFormData(prev => ({ ...prev, grossPay: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1000.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Net Pay
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.netPay}
                      onChange={(e) => setFormData(prev => ({ ...prev, netPay: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="800.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deductions
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.deductions}
                      onChange={(e) => setFormData(prev => ({ ...prev, deductions: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="200.00"
                    />
                  </div>
                </div>
              </div>

              {/* Upload Button */}
              <div className="text-center">
                <button
                  onClick={handleFileSelect}
                  disabled={uploading}
                  className={`px-6 py-3 text-white font-medium rounded-lg ${
                    uploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } transition-colors`}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                      Uploading...
                    </>
                  ) : (
                    'Select and Upload PDF'
                  )}
                </button>
                <p className="mt-2 text-sm text-gray-600">
                  Only PDF files are allowed. File will be securely stored and employee will be notified.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {view === 'manage' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading paystubs...</p>
            </div>
          ) : paystubs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No paystubs found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pay Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paystubs.map((paystub) => (
                    <tr key={paystub._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {paystub.employeeName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {paystub.employeeId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(paystub.payPeriodStart)} - {formatDate(paystub.payPeriodEnd)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Week {paystub.weekNumber}, {paystub.year}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {paystub.metadata?.totalHours || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          paystub.status === 'available'
                            ? 'bg-green-100 text-green-800'
                            : paystub.status === 'uploaded'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {paystub.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(paystub.uploadedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'batch' && (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Batch Processing</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Upload a single PDF containing multiple paystubs (~125 pages). The system will:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Automatically split the PDF into individual paystubs</li>
                    <li>Match each paystub to the correct employee using AI text recognition</li>
                    <li>Save individual paystubs to secure storage</li>
                    <li>Send notification emails to all employees</li>
                    <li>Generate a detailed processing report</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Pay Period Information for Batch */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Batch Processing Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pay Period Start Date *
                </label>
                <input
                  type="date"
                  value={formData.payPeriodStart}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pay Period End Date *
                </label>
                <input
                  type="date"
                  value={formData.payPeriodEnd}
                  onChange={(e) => setFormData(prev => ({ ...prev, payPeriodEnd: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Week Number
                </label>
                <input
                  type="number"
                  value={formData.weekNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, weekNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Auto-calculated"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Batch Upload Button */}
          <div className="text-center">
            <button
              onClick={handleFileSelect}
              disabled={batchProcessing}
              className={`px-8 py-4 text-white font-medium rounded-lg ${
                batchProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              } transition-colors`}
            >
              {batchProcessing ? (
                <>
                  <div className="animate-spin inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                  Processing Batch PDF...
                </>
              ) : (
                'Select Batch PDF (125+ Paystubs)'
              )}
            </button>
            <p className="mt-2 text-sm text-gray-600">
              Upload the encrypted PDF with all employee paystubs. Processing may take several minutes.
            </p>
          </div>

          {/* Batch Results Display */}
          {batchResults && (
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Batch Processing Results</h3>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{batchResults.savedPaystubs}</div>
                  <div className="text-sm text-gray-600">Successfully Processed</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{batchResults.unmatched?.length || 0}</div>
                  <div className="text-sm text-gray-600">Unmatched Pages</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{batchResults.missingEmployees?.length || 0}</div>
                  <div className="text-sm text-gray-600">Missing Employees</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{batchResults.notifications || 0}</div>
                  <div className="text-sm text-gray-600">Notifications Sent</div>
                </div>
              </div>

              {/* Successful Matches */}
              {batchResults.processedEmployees && batchResults.processedEmployees.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">Successfully Processed ({batchResults.processedEmployees.length})</h4>
                  <div className="max-h-40 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {batchResults.processedEmployees.map((emp, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-green-50 rounded border">
                          <span className="text-sm">{emp.name} ({emp.employeeId})</span>
                          <span className="text-xs text-green-600 font-medium">{emp.confidence}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Unmatched Pages */}
              {batchResults.unmatched && batchResults.unmatched.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">Unmatched Pages ({batchResults.unmatched.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {batchResults.unmatched.map((unmatched, index) => (
                      <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="text-sm font-medium text-yellow-800">Page {unmatched.pageNumber}</div>
                        <div className="text-xs text-gray-600 mt-1">{unmatched.reason}</div>
                        <div className="text-xs text-gray-500 mt-2 truncate">{unmatched.textPreview}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Employees */}
              {batchResults.missingEmployees && batchResults.missingEmployees.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">Employees Without Paystubs ({batchResults.missingEmployees.length})</h4>
                  <div className="max-h-40 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {batchResults.missingEmployees.map((emp, index) => (
                        <div key={index} className="p-2 bg-red-50 rounded border border-red-200">
                          <div className="text-sm font-medium text-red-800">{emp.employeeName}</div>
                          <div className="text-xs text-gray-600">ID: {emp.employeeId}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setBatchResults(null)}
                className="mt-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Clear Results
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPaystubManagement;